import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Spinner } from '../../components/atoms/Spinner'
import { useAuth } from '../../../context/AuthContext'
import { useTenant } from '../../../context/TenantContext'
import { PASSWORD } from '../../../constants/password.constants'
import { ROUTES } from '../../../constants/routes.constants'
import { isValidEmail } from '../../../constants/validators.constants'
import { Reveal } from '../../motion'

/**
 * `/account/register` — customer sign-up with email verification.
 *
 * Locked flow (Kiran 2026-05-14, ordering finalized in same session):
 *
 *   STAGE 1 — Identity
 *     User types First name, Last name, Email (in that order).
 *     "Verify Email" enables once: names non-empty AND email format valid.
 *     Cancel returns to home. POST /auth/email/verify-request sends the
 *     code; server stores it in KV with 5-min TTL + enqueues the email
 *     notification (consumer sends via Resend).
 *
 *   STAGE 2 — Verify code
 *     6 single-digit boxes with auto-advance + paste + MM:SS countdown
 *     to the server-supplied expiresAt. Timer → 0 → "Resend code" link
 *     appears (calls verify-request again, KV overwrites the entry).
 *
 *   STAGE 3 — Password
 *     After /auth/email/verify-confirm passes, password + confirm fields
 *     enable. Sign Up gates on (password >= 8 + passwords match).
 *     Submits POST /auth/register with the full {firstName, lastName,
 *     email, password} payload. Backend re-checks the verified-email
 *     KV marker; on success it consumes the marker, INSERTs into
 *     credentials + potential_customer, sets the session cookie, returns
 *     the user_types.landing_path (Rule #23). Page navigates there.
 *
 *   Stage 1+2 inputs stay visible (disabled) in later stages so the
 *   user can see what they committed to. Cancel is always available.
 */

const NAMEMAXLEN = 64
const CODELENGTH = 6
const ORGANISATION_URL_MAXLEN = 256

type Stage = 'identity' | 'code' | 'password'

export function RegisterPage(): JSX.Element {
  const navigate = useNavigate()
  const {
    isAuthenticated,
    isLoading: bootLoading,
    register,
    requestEmailVerification,
    confirmEmailVerification
  } = useAuth()
  // Tenant-aware sign-up gating (Kiran 2026-06-08):
  // master apex → ask for the Organisation URL (the visitor is creating
  // a new org that may be Built into a tenant).
  // tenant subdomain → skip the URL field + the URL-vs-email-domain
  // check; the new customer is bound to the subdomain's tenant org for
  // life and the URL isn't useful in that flow.
  const tenant = useTenant()
  const orgUrlRequired = tenant.isMaster

  // ---------- form state ----------
  const [stage, setStage] = useState<Stage>('identity')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [organisationUrl, setOrganisationUrl] = useState('')
  const [digits, setDigits] = useState<string[]>(() => Array(CODELENGTH).fill(''))
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [secondsLeft, setSecondsLeft] = useState(0)

  // refs for the 6 digit inputs (auto-advance + paste)
  const digitRefs = useRef<Array<HTMLInputElement | null>>([])

  // ---------- countdown ----------
  useEffect(() => {
    if (expiresAt === null) return
    const tick = (): void => {
      const ms = new Date(expiresAt).getTime() - Date.now()
      setSecondsLeft(Math.max(0, Math.ceil(ms / 1000)))
    }
    tick()
    const handle = window.setInterval(tick, 1000)
    return () => window.clearInterval(handle)
  }, [expiresAt])

  // ---------- stage 1 → 2 ----------
  const requestCode = useCallback(async (): Promise<void> => {
    setSubmitError(null)
    setBusy(true)
    try {
      // On a tenant subdomain skip the orgUrl arg — the verify-email
      // handler then takes its "no URL = no URL gates" branch.
      const data = orgUrlRequired
        ? await requestEmailVerification(email.trim(), organisationUrl.trim())
        : await requestEmailVerification(email.trim())
      setExpiresAt(data.expiresAt)
      setDigits(Array(CODELENGTH).fill(''))
      setStage('code')
      window.setTimeout(() => digitRefs.current[0]?.focus(), 0)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not send verification code')
    } finally {
      setBusy(false)
    }
  }, [email, organisationUrl, orgUrlRequired, requestEmailVerification])

  // ---------- stage 2 → 3 ----------
  const confirmCode = useCallback(async (code: string): Promise<void> => {
    setSubmitError(null)
    setBusy(true)
    try {
      await confirmEmailVerification(email.trim(), code)
      setStage('password')
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Incorrect or expired code')
    } finally {
      setBusy(false)
    }
  }, [email, confirmEmailVerification])

  // ---------- final submit ----------
  const submitRegistration = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault()
      setSubmitError(null)
      setBusy(true)
      try {
        const landingPath = await register({
          email: email.trim(),
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          acceptsMarketing: false,
          // organisationUrl omitted on tenant subdomains — the backend's
          // register handler enforces the same rule and skips URL gates
          // when the resolved tenant is non-master.
          ...(orgUrlRequired ? { organisationUrl: organisationUrl.trim() } : {})
        })
        navigate(landingPath, { replace: true })
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Sign-up failed')
      } finally {
        setBusy(false)
      }
    },
    [email, password, firstName, lastName, organisationUrl, orgUrlRequired, register, navigate]
  )

  // ---------- digit input wiring ----------
  const handleDigitChange = (idx: number, value: string): void => {
    const cleaned = value.replace(/\D/g, '').slice(0, 1)
    setDigits((prev) => {
      const next = [...prev]
      next[idx] = cleaned
      return next
    })
    if (cleaned !== '' && idx < CODELENGTH - 1) {
      digitRefs.current[idx + 1]?.focus()
    }
    if (cleaned !== '' && idx === CODELENGTH - 1) {
      const joined = digits.map((d, i) => (i === idx ? cleaned : d)).join('')
      if (joined.length === CODELENGTH) void confirmCode(joined)
    }
  }

  const handleDigitKeyDown = (
    idx: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (e.key === 'Backspace' && digits[idx] === '' && idx > 0) {
      digitRefs.current[idx - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODELENGTH)
    if (pasted.length === 0) return
    e.preventDefault()
    const next = Array(CODELENGTH).fill('') as string[]
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i]!
    setDigits(next)
    if (pasted.length === CODELENGTH) {
      void confirmCode(pasted)
    } else {
      digitRefs.current[Math.min(pasted.length, CODELENGTH - 1)]?.focus()
    }
  }

  // ---------- guards (after every hook to honour rules-of-hooks) ----------
  if (bootLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }
  if (isAuthenticated) {
    return <Navigate to={ROUTES.ACCOUNT} replace />
  }

  // ---------- gating ----------
  const firstNameOk = firstName.trim().length > 0 && firstName.trim().length <= NAMEMAXLEN
  const lastNameOk = lastName.trim().length > 0 && lastName.trim().length <= NAMEMAXLEN
  const emailOk = isValidEmail(email)
  // Org URL must start with https:// (Kiran 2026-06-08 — http:// rejected
  // brand-wide) AND contain the email's @-domain (case-insensitive
  // substring). E.g. email=kiran@magna.com matches url=https://magna.com,
  // url=https://Magna.com, url=https://www.magna.com.
  // On a tenant subdomain the URL field is hidden + the gate is
  // skipped (orgUrlOk = true) so the customer can register without it.
  const orgUrlOk = !orgUrlRequired || (() => {
    const trimmed = organisationUrl.trim()
    if (trimmed.length === 0 || trimmed.length > ORGANISATION_URL_MAXLEN) return false
    if (!/^https:\/\/\S+$/i.test(trimmed)) return false
    const url = trimmed.toLowerCase()
    const at = email.lastIndexOf('@')
    if (at < 0) return false
    const domain = email.slice(at + 1).trim().toLowerCase()
    if (domain === '') return false
    return url.includes(domain)
  })()
  const canVerify = firstNameOk && lastNameOk && emailOk && orgUrlOk && !busy

  const passwordOk = password.length >= PASSWORD.MINLENGTH
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0
  const canSubmitFinal = passwordOk && passwordsMatch && !busy
  const mismatchHint = confirmPassword.length > 0 && !passwordsMatch

  const timerStr = formatMmSs(secondsLeft)
  const timerExpired = stage === 'code' && secondsLeft === 0 && expiresAt !== null

  function handleCancel(): void {
    navigate(ROUTES.HOME)
  }

  return (
    <Reveal as="div" amount={0.01} duration={1.0} travel={32}>
      <Helmet>
        <title>Create account — Uyyamadilu</title>
      </Helmet>
      <h1 className="font-heading text-h2 text-charcoal mb-2">Create an account</h1>
      <p className="font-body text-body-sm text-text-secondary mb-6">
        We&rsquo;ll email a 6-digit code to confirm your email, then you set a password.
      </p>

      {submitError !== null ? (
        <div className="mb-4">
          <Alert tone="error">{submitError}</Alert>
        </div>
      ) : null}

      {/* ─────────── STAGE 1: identity ─────────── */}
      <div className="space-y-4">
        <label className="block">
          <span className="font-body text-body-sm text-text-secondary block mb-1">First name</span>
          <Input
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={stage !== 'identity' || busy}
            maxLength={NAMEMAXLEN}
            required
          />
        </label>
        <label className="block">
          <span className="font-body text-body-sm text-text-secondary block mb-1">Last name</span>
          <Input
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={stage !== 'identity' || busy}
            maxLength={NAMEMAXLEN}
            required
          />
        </label>
        <label className="block">
          <span className="font-body text-body-sm text-text-secondary block mb-1">Email</span>
          <Input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={stage !== 'identity' || busy}
            required
          />
        </label>
        {orgUrlRequired ? (
          <label className="block">
            <span className="font-body text-body-sm text-text-secondary block mb-1">
              Organisation URL
            </span>
            <Input
              type="url"
              autoComplete="url"
              inputMode="url"
              value={organisationUrl}
              onChange={(e) => setOrganisationUrl(e.target.value)}
              disabled={stage !== 'identity' || busy}
              maxLength={ORGANISATION_URL_MAXLEN}
              placeholder="https://www.example.com"
              required
            />
            {stage === 'identity' && organisationUrl.length > 0 && emailOk && !orgUrlOk ? (
              <span className="block text-body-xs font-body text-terra mt-1">
                Organisation URL must start with https:// and match the email address domain.
              </span>
            ) : null}
          </label>
        ) : null}
      </div>

      {stage === 'identity' ? (
        <div className="flex flex-col sm:flex-row gap-2 pt-4">
          <Button
            type="button"
            variant="primary"
            size="lg"
            disabled={!canVerify}
            onClick={() => void requestCode()}
            className="flex-1"
          >
            {busy ? 'Sending…' : 'Verify Email'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={handleCancel}
            disabled={busy}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      ) : null}

      {/* ─────────── STAGE 2: code ─────────── */}
      {stage === 'code' || stage === 'password' ? (
        <div className="mt-4 mb-4">
          <span className="font-body text-body-sm text-text-secondary block mb-2">
            Enter the 6-digit code sent to your email
          </span>
          <div className="flex gap-2 justify-between" onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => {
                  digitRefs.current[i] = el
                }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]"
                maxLength={1}
                value={d}
                disabled={stage !== 'code' || busy}
                onChange={(e) => handleDigitChange(i, e.target.value)}
                onKeyDown={(e) => handleDigitKeyDown(i, e)}
                className="w-11 h-12 sm:w-12 sm:h-14 text-center text-h3 font-heading rounded-md border border-border bg-bg-primary focus:outline-none focus:border-forest disabled:bg-parchment/40 disabled:text-text-secondary"
                aria-label={`Digit ${i + 1}`}
              />
            ))}
          </div>
          {stage === 'code' ? (
            <div className="flex items-center justify-between mt-3">
              {timerExpired ? (
                <button
                  type="button"
                  onClick={() => void requestCode()}
                  className="font-body text-body-sm text-forest underline-offset-2 hover:underline disabled:text-text-secondary disabled:no-underline"
                  disabled={busy}
                >
                  Code expired — Resend code
                </button>
              ) : (
                <span className="font-body text-body-sm text-text-secondary">
                  Expires in <span className="font-mono text-charcoal">{timerStr}</span>
                </span>
              )}
              <button
                type="button"
                onClick={() => {
                  setStage('identity')
                  setExpiresAt(null)
                  setDigits(Array(CODELENGTH).fill(''))
                  setSubmitError(null)
                }}
                className="font-body text-body-sm text-text-secondary hover:text-charcoal"
                disabled={busy}
              >
                Edit details
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* ─────────── STAGE 3: password ─────────── */}
      {stage === 'password' ? (
        <form onSubmit={submitRegistration} className="space-y-4 mt-4" noValidate>
          <label className="block">
            <span className="font-body text-body-sm text-text-secondary block mb-1">
              Password (8+ characters)
            </span>
            <Input
              type="password"
              autoComplete="new-password"
              minLength={PASSWORD.MINLENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <label className="block">
            <span className="font-body text-body-sm text-text-secondary block mb-1">
              Confirm password
            </span>
            <Input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              hasError={mismatchHint}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            {mismatchHint ? (
              <span className="block text-body-xs font-body text-terra mt-1">
                Passwords don&rsquo;t match.
              </span>
            ) : null}
          </label>
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={!canSubmitFinal}
              className="flex-1"
            >
              {busy ? 'Creating account…' : 'Sign Up'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              onClick={handleCancel}
              disabled={busy}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : null}

      <p className="mt-6 text-body-sm text-text-secondary">
        Already have an account?{' '}
        <Link to={ROUTES.LOGIN} className="text-forest underline-offset-2 hover:underline">
          Sign in
        </Link>
      </p>
    </Reveal>
  )
}

function formatMmSs(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
